"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";

export interface AnimationProps {
	children: ReactNode;
	className?: string;
}

export interface StaggerProps extends AnimationProps {
	staggerDelay?: number;
	initialDelay?: number;
}

export const FadeIn = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

export const SlideUp = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 20 }}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

export const SlideInLeft = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, x: -20 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: -20 }}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

export const SlideInRight = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, x: 20 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: 20 }}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

export const StaggerContainer = ({
	children,
	className,
	staggerDelay = 0.1,
	initialDelay = 0,
}: StaggerProps) => {
	return (
		<motion.div
			initial="hidden"
			animate="visible"
			className={className}
			variants={{
				hidden: { opacity: 0 },
				visible: {
					opacity: 1,
					transition: {
						staggerChildren: staggerDelay,
						delayChildren: initialDelay,
					},
				},
			}}
		>
			{children}
		</motion.div>
	);
};

export const StaggerItem = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			variants={{
				hidden: { opacity: 0, y: 20 },
				visible: { opacity: 1, y: 0 },
			}}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

export const ScaleIn = ({ children, className }: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, scale: 0.9 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.9 }}
			transition={{ duration: 0.5 }}
			className={className}
		>
			{children}
		</motion.div>
	);
};

// New animation specifically for testimonial cards to maintain equal heights
export const TestimonialAnimation = ({
	children,
	className,
}: AnimationProps) => {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			viewport={{ once: true }}
			className={className}
			style={{ height: "100%" }}
		>
			{children}
		</motion.div>
	);
};

export const StaggeredSlideInRight = ({
	children,
	className,
}: AnimationProps) => {
	return (
		<motion.div
			variants={{
				hidden: { opacity: 0, x: 20 },
				visible: {
					opacity: 1,
					x: 0,
					transition: {
						type: "spring",
						damping: 12,
						stiffness: 80,
						duration: 0.8,
					},
				},
			}}
			className={className}
		>
			{children}
		</motion.div>
	);
};
